import { useContext, useEffect, useMemo, useRef, useState, Suspense, createContext } from "react";
import { Route, Routes, matchPath, useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RestAPI } from "@aws-amplify/api-rest";
import cloneDeep from "lodash/cloneDeep";

import { PanelMenu } from "primereact/panelmenu";
import { faExclamationCircle, faFileAlt, faPoll, faStore, faTruck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { FarmContext } from "../../contexts/FarmContext";
import { applyFormula, applyFormulas } from "../../utils/area";
import { dateDiffInMinutes } from "../../utils/date";
import switchTipoNodo from "../../utils/switchTipoNodo";
import getBaseLangs from "../../utils/language";
import withName from "../../hoc/withName";

import { icon } from "@fortawesome/fontawesome-svg-core";
import { faCrosshairs, faCubes, faSlash, faTimesCircle, faKey, faDownload, faTint, faSlidersH } from "@fortawesome/free-solid-svg-icons";
import DGAWell from "../../components/DGAWell/DGAWell";
import { putLog } from "../../utils/logcache";
import Loading from "../../img/loading-borde-suave.gif";

import Node, { formatPortsData } from "../../components/Node/Node";
import Zone from "../../components/Zone/Zone";
import Pump from "../../components/Pump/Pump";
import FarmInfo from "../../components/FarmInfo/FarmInfo";
import Hydraulic from "../../components/Hydraulic/Hydraulic";
import Sensor from "../../components/Sensor/Sensor";
import Actuator from "../../components/Actuator/Actuator";
import APITerceros from "../../components/APITerceros/APITerceros";
import User from "../../components/User/User";
import Users from "../../components/Users/Users";
import RAWData from "../../components/RAWData/RAWData";
import ErrorBoundary from "../../components/Utils/ErrorBoundary";
import LoadingDialog from "../../components/Utils/LoadingDialog";
import Graficador from "../../components/Graficador/Graficador";
import Integradores from "../../components/Integradores/Integradores";
import ExternalProvider from "../../components/ExternalProvider/ExternalProvider";
import MapaConfig from "../../components/FarmInfo/MapaConfig";
import ImageAligner from "../../components/ExternalProvider/ImageAligner";
import SystemEvents from "../../components/SystemEvents/SystemEvents";
import Phenology from "../../components/Phenology/Phenology";
import Reports from "../../components/Reports/Reports";
import BillingPolicyAndTerms from "../../components/BillingPolicyAndTerms/BillingPolicyAndTerms";
import BillingParams from "../../components/BillingParams/BillingParams";

import Marketplace from "../../components/Marketplace/Marketplace";
import ConnectionDetail from "../../components/Marketplace/ConnectionDetail";
import MarketplaceTabs from "../../components/Marketplace/MarketplaceTabs";

const [noDataWhiteBlue] = icon(faTimesCircle, { classes: ["fa-stack-1x"] }).html;
const [noLocationWhiteBlue] = icon(faCrosshairs, { classes: ["fa-stack-1x"] }).html;
const [iconBanWhiteBlue] = icon(faSlash, { classes: ["fa-stack-1x"], }).html;

const getParams = (location) => {
    const params = new URLSearchParams(location && location.search);
    const farm = params.get("farm") || params.get("farmblack");
    return { farm };
};

const defaultPrefijo = import.meta.env.VITE_DEFAULT_PREFIJO_CAMPO;
const AREA_FORMAT_KEY = "number.dec.4";
const enableMarketplace = false;
const tipoHidraulicaCount = (hidraulicas, objetoHidraulicas) => {
    hidraulicas.forEach(hidraulica => {
        if (hidraulica.tipo === 3) { //fuente
            ++objetoHidraulicas.tipoFuente;
        } else if (hidraulica.tipo === 1) { //bomba
            ++objetoHidraulicas.tipoBomba;
        } else if (hidraulica.tipo === 4) { //estanque
            ++objetoHidraulicas.tipoEstanque;
        } else if (hidraulica.tipo === 6) { //inyectoresF
            ++objetoHidraulicas.tipoInyectorF;
        } else if (hidraulica.tipo === 5) { //inyectoresPH
            ++objetoHidraulicas.tipoInyectorPH;
        } else if (hidraulica.tipo === 16) { //retrolavado
            ++objetoHidraulicas.tipoRetrolavado;
        } else if (hidraulica.tipo === 2) { //valvulas
            if (hidraulica.es_monitoreo) ++objetoHidraulicas.tipoValvulaMonitoreo;
            else ++objetoHidraulicas.tipoValvula;
        } else if (hidraulica.tipo === 9) { //pozos
            ++objetoHidraulicas.tipoPozo;
        } else if (hidraulica.tipo === 14) { //vfd
            ++objetoHidraulicas.tipoVFD;
        }
    });
};

const formatV1 = (data, areaInfo, thisNode, mapa, husoHorario, noSubChild) => {
    const items = [];

    thisNode.id = `nodo-${thisNode.nodo_id}`;
    thisNode.items = items;

    const dataNodo = mapa && mapa.nodos && mapa.nodos[thisNode.nodo_id];
    thisNode.noLocation = (!dataNodo || dataNodo.lat === undefined || dataNodo.lng === undefined) ? true : false;

    thisNode.noData = husoHorario && thisNode.fecha_ultimo_dato && dateDiffInMinutes(thisNode.fecha_ultimo_dato, husoHorario) >= 180;
    if (noSubChild) return;

    const arrChildHidraulicasNodo = data.equipos.reduce((result, equipo) => {
        equipo.hidraulicas.forEach((hidraulica) => {
            if (hidraulica.nodo_id === thisNode.nodo_id) {
                if (!hidraulica.items) hidraulica.items = [];
                result.push({
                    ...hidraulica,
                    id: `hidraulica-${hidraulica.hidraulica_id}_${thisNode.id}`,
                    etiqueta: "hijo"
                });
            }
        });
        return result;
    }, []);
    arrChildHidraulicasNodo.sort((a, b) => (a.ordenacion < b.ordenacion) ? -1 : 1);
    thisNode.hidraulicas = arrChildHidraulicasNodo;

    const hidraulicasCantidad = (Array.isArray(thisNode.hidraulicas) && thisNode.hidraulicas.length) || 0;
    const objetoChildHidraulicasNodo = {
        id: `hidraulicas-${thisNode.nodo_id}`, label: "HYDRAULICS",
        items: thisNode.hidraulicas, etiqueta: "titulo", cantidad: hidraulicasCantidad,
        tipoFuente: 0,
        tipoBomba: 0,
        tipoEstanque: 0,
        tipoInyectorF: 0,
        tipoInyectorPH: 0,
        tipoRetrolavado: 0,
        tipoValvula: 0,
        tipoValvulaMonitoreo: 0,
        tipoPozo: 0,
        tipoVFD: 0
    };
    if (hidraulicasCantidad) tipoHidraulicaCount(thisNode.hidraulicas, objetoChildHidraulicasNodo);
    items.push(objetoChildHidraulicasNodo);

    const actuadoresCantidad = (Array.isArray(thisNode.actuadores) && thisNode.actuadores.length) || 0;
    const childActuadores = {
        id: `actuadores-${thisNode.nodo_id}`, label: "ACTUATORS",
        items: thisNode.actuadores, etiqueta: "titulo", cantidad: actuadoresCantidad
    };
    if (actuadoresCantidad) thisNode.actuadores.forEach(a => {
        a.id = `actuador-${a.actuador_id}`;
        a.etiqueta = "hijo";
        const hidraulica = arrChildHidraulicasNodo.find((h) =>
            h.actuadores && h.actuadores.some((r) => r && r.actuadorId === a.actuador_id)
        );
        if (hidraulica) {
            a.tipo_asociacion_ha = hidraulica.tipo_asociacion_ha && hidraulica.tipo_asociacion_ha[a.actuador_id];
            a.hidraulica = hidraulica.nombre;
        }
    });
    items.unshift(childActuadores);

    const sensoresCantidad = (Array.isArray(thisNode.sensores) && thisNode.sensores.length) || 0;
    const childSensores = {
        id: `sensores-${thisNode.nodo_id}`, label: "SENSORS",
        items: thisNode.sensores, etiqueta: "titulo", cantidad: sensoresCantidad
    };
    if (sensoresCantidad) thisNode.sensores.forEach(s => {
        s.id = `sensor-${s.sensor_id}`;
        s.etiqueta = "hijo";
        if (s.profundidad) {
            s.centi_unidad = areaInfo && areaInfo.centi_unidad || "";
        } else if (s.centi_unidad) {
            delete s.centi_unidad;
        }
    });
    items.push(childSensores);

    return thisNode;
};
const formatNodo = (data, areaInfo, thisNode, mapa, husoHorario, noSubChild) => {
    const items = [];

    thisNode.id = `nodo-${thisNode.nodo_id}`;
    thisNode.items = items;

    const dataNodo = mapa && mapa.nodos && mapa.nodos[thisNode.nodo_id];
    thisNode.noLocation = (!dataNodo || dataNodo.lat === undefined || dataNodo.lng === undefined) ? true : false;

    thisNode.noData = husoHorario && thisNode.fecha_ultimo_dato && dateDiffInMinutes(thisNode.fecha_ultimo_dato, husoHorario) >= 180;
    if (noSubChild) return;

    const arrChildHidraulicasNodo = data.equipos.reduce((result, equipo) => {
        equipo.hidraulicas.forEach((hidraulica) => {
            if (hidraulica.nodo_id === thisNode.nodo_id) {
                if (!hidraulica.items) hidraulica.items = [];
                result.push({
                    ...hidraulica,
                    id: `hidraulica-${hidraulica.hidraulica_id}_${thisNode.id}`,
                    etiqueta: "hijo"
                });
            }
        });
        return result;
    }, []);
    arrChildHidraulicasNodo.sort((a, b) => (a.ordenacion < b.ordenacion) ? -1 : 1);
    thisNode.hidraulicas = arrChildHidraulicasNodo;

    const hidraulicasCantidad = (Array.isArray(thisNode.hidraulicas) && thisNode.hidraulicas.length) || 0;
    const objetoChildHidraulicasNodo = {
        id: `hidraulicas-${thisNode.nodo_id}`, label: "HYDRAULICS",
        items: thisNode.hidraulicas, etiqueta: "titulo", cantidad: hidraulicasCantidad,
        tipoFuente: 0,
        tipoBomba: 0,
        tipoEstanque: 0,
        tipoInyectorF: 0,
        tipoInyectorPH: 0,
        tipoRetrolavado: 0,
        tipoValvula: 0,
        tipoValvulaMonitoreo: 0,
        tipoPozo: 0,
        tipoVFD: 0
    };
    if (hidraulicasCantidad) tipoHidraulicaCount(thisNode.hidraulicas, objetoChildHidraulicasNodo);
    items.push(objetoChildHidraulicasNodo);

    const actuadoresCantidad = (Array.isArray(thisNode.actuadores) && thisNode.actuadores.length) || 0;
    const childActuadores = {
        id: `actuadores-${thisNode.nodo_id}`, label: "ACTUATORS",
        items: thisNode.actuadores, etiqueta: "titulo", cantidad: actuadoresCantidad
    };
    if (actuadoresCantidad) thisNode.actuadores.forEach(a => {
        a.id = `actuador-${a.actuador_id}`;
        a.etiqueta = "hijo";
        const hidraulica = arrChildHidraulicasNodo.find((h) =>
            h.actuadores && h.actuadores.some((r) => r && r.actuadorId === a.actuador_id)
        );
        if (hidraulica) {
            a.tipo_asociacion_ha = hidraulica.tipo_asociacion_ha && hidraulica.tipo_asociacion_ha[a.actuador_id];
            a.hidraulica = hidraulica.nombre;
        }
    });
    items.unshift(childActuadores);

    const sensoresCantidad = (Array.isArray(thisNode.sensores) && thisNode.sensores.length) || 0;
    const childSensores = {
        id: `sensores-${thisNode.nodo_id}`, label: "SENSORS",
        items: thisNode.sensores, etiqueta: "titulo", cantidad: sensoresCantidad
    };
    if (sensoresCantidad) thisNode.sensores.forEach(s => {
        s.id = `sensor-${s.sensor_id}`;
        s.etiqueta = "hijo";
        if (s.profundidad) {
            s.centi_unidad = areaInfo && areaInfo.centi_unidad || "";
        } else if (s.centi_unidad) {
            delete s.centi_unidad;
        }
    });
    items.push(childSensores);
    thisNode.v1_childs = thisNode?.v1_childs?.map(nodev1 => {
        const nodeIndex = data.nodes.findIndex(nodo => nodo.nodo_id === nodev1);
        if (nodeIndex === -1) return null;
        return formatV1(data, areaInfo, data.nodes[nodeIndex], mapa, husoHorario);
    });
};

export const formatData = (data, areaInfo, users, saas, mapa, husoHorario, can_edit, can_billing) => {
    const { pozos } = data;
    if (!data.equipos) data.equipos = [];
    data.equipos.forEach((equipo) => {
        equipo.hidraulicas.forEach((hidraulica) => {
            if (hidraulica.tipo === 3) { // fuente
                hidraulica.ordenacion = 1;
            } else if (hidraulica.tipo === 1) { // bomba
                hidraulica.ordenacion = 2;
            } else if (hidraulica.tipo === 4) { // estanque
                hidraulica.ordenacion = 3;
            } else if (hidraulica.tipo === 6) { // inyectoresF
                hidraulica.ordenacion = 4;
            } else if (hidraulica.tipo === 5) { // inyectoresPH
                hidraulica.ordenacion = 5;
            } else if (hidraulica.tipo === 16) { // retrolavado
                hidraulica.ordenacion = 6;
            } else if (hidraulica.tipo === 2) { // valvulas
                hidraulica.ordenacion = 7;
            } else if (hidraulica.tipo === 9) { // pozos
                hidraulica.ordenacion = 8;
            } else if (hidraulica.tipo === 14) { // VFD
                hidraulica.ordenacion = 9;
            } else {
                hidraulica.ordenacion = 10;
            }
        });
    });

    const network = data.network || [];
    //buscar GW
    const GWsEncontrados = network.reduce((result, gwId) => {
        const gwIdx = data.nodes.findIndex(nodo => nodo.nodo_id === gwId);
        if (gwIdx === -1) return result;
        const gw = data.nodes[gwIdx];
        result.push(gw);

        formatNodo(data, areaInfo, gw, mapa, husoHorario, !gw.nodo_id && !gw.actuadores && !gw.sensores && !gw.equipos);
        //gw.expanded = true;
        gw.expanded = false;
        gw.etiqueta = "GW";
        if (Array.isArray(gw.childs)) {
            gw.childs.forEach((child) => {
                const childNode = data.nodes.find(n => n.nodo_id === child);
                if (!childNode || childNode.c1_v1) return;
                gw.items.push(childNode);
                formatNodo(data, areaInfo, childNode, mapa, husoHorario);
            });

            gw.cantNodos = gw.childs.length;
        }
        return result;
    }, []);

    if (data && Array.isArray(data.sectores) && data.sectores.length > 0) data.sectores.sort((a, b) => (a.orden < b.orden) ? -1 : 1);

    let cantGw = 0;
    let cantNoGw = 0;

    if (Array.isArray(GWsEncontrados) && GWsEncontrados.length === 1) {
        const gwNode = GWsEncontrados[0];
        let isNotNode;

        if (gwNode.items.length > 0) {
            gwNode.items = gwNode.items.filter(nodo => {
                isNotNode = nodo.id.search("nodo") === -1;
                if (!isNotNode) GWsEncontrados.push(nodo);
                return isNotNode;
            });
        }
    }

    if (GWsEncontrados.length > 0) {
        GWsEncontrados.forEach((nodo) => {
            if (nodo.is_gw || nodo.etiqueta === "GW") {
                cantGw += 1;
            }
            if (!nodo.is_gw && !nodo.etiqueta) {
                cantNoGw += 1;
            }
        });

        //count nogw after gws counter
        GWsEncontrados.forEach((nodo) => {
            if (Array.isArray(nodo.childs) && cantGw > 1) {
                cantNoGw = cantNoGw + nodo.childs.length;
                /* nodo.cantGw = nodo.childs.length; */
            }

            if (cantGw < 2) {
                nodo.cantNodos = null;
            }
        });
        GWsEncontrados.forEach((nodo) => {
            const v1_childs_cantidad = (Array.isArray(nodo.v1_childs) && nodo.v1_childs.length) || 0;
            nodo.cantNodos = v1_childs_cantidad;
            if (v1_childs_cantidad){
                nodo.v1_childs.forEach(n => {
                    if(n.c1_v1 === nodo.nodo_id){
                        nodo.items.unshift({
                            id:`nodo-${n.nodo_id}`,
                            etiqueta: "titulo",
                            ...n,
                            cantidad:null
                        });

                    }
                });
            }
        });
    }

    const hidraulicaToEquipo = Array.isArray(data.equipos) && data.equipos.reduce((res, { equipo_id, hidraulicas }) => {
        if (!Array.isArray(hidraulicas)) return res;
        hidraulicas.forEach((hidraulica) => {
            res[hidraulica.hidraulica_id] = equipo_id;
        });
        return res;
    }, {});

    // AÑADIR SENSORES, Y TRANSFORMAR AREA EN SECTORES
    if (!data.sectores) data.sectores = [];
    for (const sector of data.sectores) {
        const sectorChilds = [];
        sector.id = `sector-${sector.sector_id}`;
        sector.etiqueta = "hijo";
        if (sector.superficie) {
            sector.kilo_unidad = areaInfo && areaInfo.kilo_unidad || "";
        } else if (sector.kilo_unidad) {
            delete sector.kilo_unidad;
        }
        const dataSector = mapa && mapa.sectores && mapa.sectores[sector.sector_id];
        sector.noLocation = (!dataSector || !Array.isArray(dataSector.path) || dataSector.path.length < 2) ? true : false;

        const sensoresSector = [];
        if (Array.isArray(sector.sensores)) {
            for (const sensorSector of sector.sensores) {
                //ENCONTRAR NODOS CON LA nodo_id de sector.sensor
                const nodoSensor = data.nodes.find((n) => n.nodo_id === sensorSector.nodoId);

                if (nodoSensor && Array.isArray(nodoSensor.sensores)) {
                    const sensorSectorEncontrado = nodoSensor.sensores.find((sensor) => sensor.sensor_id === sensorSector.sensorId);
                    if (sensorSectorEncontrado) {
                        sensoresSector.push({
                            ...sensorSectorEncontrado,
                            id: `sensor-${sensorSectorEncontrado.sensor_id}_${sector.id}`,
                            etiqueta: "hijo"
                        });
                    }
                }
            }
        }

        sectorChilds.push({
            id: `sensores-${sector.sector_id}`, label: "SENSORS",
            items: sensoresSector, etiqueta: "titulo", cantidad: sensoresSector.length
        });

        //AÑADIR HIDRAULICA A SECTORES
        const valvulasSector = [];
        if (Array.isArray(sector.hidraulicas)) {
            sector.hidraulicas.forEach((sectorHidra) => {
                //ENCONTRAR HIDRAULICAS
                const hidraulica_equipo_id = hidraulicaToEquipo[sectorHidra];
                const { hidraulicas } = (Array.isArray(data.equipos) && data.equipos.find(({ equipo_id }) => hidraulica_equipo_id === equipo_id)) || {};
                const equipoHidra = Array.isArray(hidraulicas) && hidraulicas.find(({ hidraulica_id }) => sectorHidra === hidraulica_id);
                if (equipoHidra) {
                    if (!equipoHidra.items) equipoHidra.items = [];
                    valvulasSector.push({
                        ...equipoHidra,
                        id: `hidraulica-${equipoHidra.hidraulica_id}_${sector.id}`,
                        etiqueta: "hijo"
                    });
                }
            });
        }
        valvulasSector.sort((a, b) => (a.ordenacion < b.ordenacion) ? -1 : 1);
        sector.hidraulicasEncontradas = valvulasSector;

        const objetoValvulas = {
            id: `hidraulicas-${sector.sector_id}`, label: "HYDRAULICS",
            items: sector.hidraulicasEncontradas, etiqueta: "titulo", cantidad: sector.hidraulicasEncontradas.length,
            tipoFuente: 0,
            tipoBomba: 0,
            tipoEstanque: 0,
            tipoInyectorF: 0,
            tipoInyectorPH: 0,
            tipoRetrolavado: 0,
            tipoValvula: 0,
            tipoValvulaMonitoreo: 0,
            tipoPozo: 0,
            tipoVFD: 0
        };
        tipoHidraulicaCount(sector.hidraulicasEncontradas, objetoValvulas);
        sectorChilds.push(objetoValvulas);

        sector.items = sectorChilds;
        sector.tipo = sector.tipo.sort((a, b) => a - b);
    }

    //ENCONTRAR SECTORES ASIGNADOS A LOS EQUIPOS
    for (const equipo of data.equipos) {
        const sectoresEquipo = data.sectores.reduce((result, sector) => {
            sector.equipo.forEach((id) => {
                if (id && id === equipo.equipo_id) {
                    result.push({
                        ...sector,
                        id: `sector-${sector.sector_id}_equipo-${id}`,
                        etiqueta: "hijo"
                    });
                }
            });
            return result;
        }, []);

        sectoresEquipo.sort((a, b) => (a.orden < b.orden) ? -1 : 1);
        equipo.sectores = sectoresEquipo;
        equipo.etiqueta = "hijo";
    }
    ///

    for (const equipo of data.equipos) {
    //AÑADIR RAMA DE ACTUADORES A HIDRAULICA
        for (const hidra of equipo.hidraulicas) {
            const actuadoresHidraulica = [];
            hidra.id = `hidraulica-${hidra.hidraulica_id}`;
            hidra.etiqueta = "hijo";

            if (Array.isArray(hidra.actuadores)) {
                for (const actHidra of hidra.actuadores) { //act : {nodoId: xx, actuadorId:xx}
                    const nodoAct = data.nodes.find((n) => n.nodo_id === actHidra.nodoId);

                    if (nodoAct && Array.isArray(nodoAct.actuadores)) {
                        const actuadorEncontrado = nodoAct.actuadores.find((actuador) => actuador.actuador_id === actHidra.actuadorId);
                        if (actuadorEncontrado) {
                            actuadoresHidraulica.push({
                                ...actuadorEncontrado,
                                id: `actuador-${actuadorEncontrado.actuador_id}_${hidra.id}`
                            });
                        }
                    }
                }
            }

            const itemsActuadores = {
                id: `actuadores-${hidra.hidraulica_id}`, label: "ACTUATORS",
                items: actuadoresHidraulica, etiqueta: "titulo", cantidad: actuadoresHidraulica.length
            };

            //AÑADIR RAMA DE SENSORES A HIDRAULICA
            const sensoresHidraulica = [];
            if (Array.isArray(hidra.sensores)) {
                for (const senHidra of hidra.sensores) { //sensor : {nodoId: xx, actuadorId:xx}
                    const nodoSen = data.nodes.find((n) => n.nodo_id === senHidra.nodoId);

                    if (nodoSen && Array.isArray(nodoSen.sensores)) {
                        const sensorEncontrado = nodoSen.sensores.find((sensor) => sensor.sensor_id === senHidra.sensorId);
                        if (sensorEncontrado) {
                            sensoresHidraulica.push({
                                ...sensorEncontrado,
                                etiqueta: "hijo",
                                id: `sensor-${sensorEncontrado.sensor_id}_${hidra.id}`
                            });
                        }
                    }
                }
            }

            const itemsSensores = {
                id: `sensores-${hidra.hidraulica_id}`, label: "SENSORS",
                items: sensoresHidraulica, etiqueta: "titulo", cantidad: sensoresHidraulica.length
            };

            //AÑADIR RAMA DE SECTORES A HIDRAULICAS TIPO 6 - INYECTOR DE FERTILIZANTE
            const items_Sector = {};
            if (hidra.tipo === 6) {
                const arrSectores = [];
                equipo.hidraulicas.forEach(padre => {
                    if (Array.isArray(padre.hijos) && padre.hijos.includes(hidra.hidraulica_id)) {
                        if (Array.isArray(equipo.sectores) && equipo.sectores.length > 0) {
                            equipo.sectores.forEach(sector => {
                                if (Array.isArray(sector.hidraulicas) && sector.hidraulicas.length > 0) {
                                    if (sector.hidraulicas.includes(padre.hidraulica_id) && !arrSectores.includes(sector)) {
                                        arrSectores.push(sector);
                                    }
                                }
                            });
                        }
                    }
                });

                if (arrSectores.length > 0) {
                    arrSectores.forEach(s => {
                        s.id = `sector-${s.sector_id}_${hidra.id}`;
                    });
                }
                items_Sector.id = `sectores-${hidra.id}`;
                items_Sector.etiqueta = "titulo";
                items_Sector.label = "ZONES";
                items_Sector.cantidad = arrSectores.length;
                items_Sector.items = arrSectores;
            }

            //
            if (!hidra.items) hidra.items = [];

            if (Object.entries(items_Sector).length > 0) {
                hidra.items.push(itemsActuadores, itemsSensores, items_Sector);
            } else {
                hidra.items.push(itemsActuadores, itemsSensores);
            }

        }
        equipo.hidraulicas.sort((a, b) => (a.ordenacion < b.ordenacion) ? -1 : 1);

        const equipoSectoresHidraulicas = [];
        if (Array.isArray(equipo.hidraulicas)) {
            const objetoHidraulicas = {
                id: `hidraulicas-${equipo.equipo_id}`, label: "HYDRAULICS",
                items: equipo.hidraulicas, etiqueta: "titulo", cantidad: equipo.hidraulicas.length,
                tipoFuente: 0,
                tipoBomba: 0,
                tipoEstanque: 0,
                tipoInyectorF: 0,
                tipoInyectorPH: 0,
                tipoRetrolavado: 0,
                tipoValvula: 0,
                tipoValvulaMonitoreo: 0,
                tipoPozo: 0,
                tipoVFD: 0
            };
            tipoHidraulicaCount(equipo.hidraulicas, objetoHidraulicas);
            equipoSectoresHidraulicas.push(objetoHidraulicas);

            const arrNodosParaEquipo = [];
            equipo.hidraulicas.forEach(h => {
                if (h.nodo_id) {
                    const nodoParaEquipo = Array.isArray(data.nodes) && data.nodes.find(nodos => nodos.nodo_id === h.nodo_id);
                    if (nodoParaEquipo && arrNodosParaEquipo.indexOf(nodoParaEquipo) === -1) arrNodosParaEquipo.push(nodoParaEquipo);
                }
            });
            const objetoNodos = {
                id: `nodos-${equipo.equipo_id}`, label: "NODES",
                items: arrNodosParaEquipo, etiqueta: "titulo", cantidad: arrNodosParaEquipo.length
            };

            equipoSectoresHidraulicas.unshift(objetoNodos);
        }

        if (Array.isArray(equipo.sectores)) {
            const objetoSectores = {
                id: `sectores-${equipo.equipo_id}`, label: "ZONES",
                items: equipo.sectores, etiqueta: "titulo", cantidad: equipo.sectores.length
            };
            equipoSectoresHidraulicas.push(objetoSectores);
        }
        equipo.id = `equipo-${equipo.equipo_id}`;
        equipo.items = equipoSectoresHidraulicas;
        equipo.etiqueta = "hijo";

        equipo.sectores.forEach((sector) => {
            sector.hidraulicasEncontradas.forEach((hidraulica) => {
                const childId = `sectores-${hidraulica.hidraulica_id}`;
                const existingIdx = hidraulica.items.findIndex(t => (t.id === childId));
                if (existingIdx >= 0) return;
                const sectoresParaHidraulica = [];

                for (const sec of equipo.sectores) {
                    // TODO: WHY DO WE HAVE 4-fors here???
                    if (sec.hidraulicas) {
                        for (const hidra of sec.hidraulicas) {
                            if (hidra === hidraulica.hidraulica_id) {
                                sectoresParaHidraulica.push({
                                    id: `sector-${sec.sector_id}_hidraulica-${hidra}`, nombre: sec.nombre, tipo: sec.tipo, etiqueta: "hijo"
                                });
                            }
                        }
                    }
                }
                hidraulica.items.push({
                    id: childId, label: "ZONES",
                    items: sectoresParaHidraulica, etiqueta: "titulo", cantidad: sectoresParaHidraulica.length
                });
                hidraulica.etiqueta = "hijo";
            });
        });
    }

    //Add zone to node
    Array.isArray(data.nodes) && data.nodes.forEach(nodo => {
        const sectoresParaNodo = [];
        Array.isArray(data.sectores) && data.sectores.forEach(sector => {
            Array.isArray(sector.sensores) && sector.sensores.forEach(sensor => {
                if (nodo.nodo_id === sensor.nodoId) {
                    if (!sectoresParaNodo.includes(sector)) {
                        sectoresParaNodo.push(sector);
                    }
                }
            });

            sector.hidraulicasEncontradas.length > 0 && sector.hidraulicasEncontradas.forEach(hidra => {
                if (nodo.nodo_id === hidra.nodo_id) {
                    if (!sectoresParaNodo.includes(sector)) {
                        sectoresParaNodo.push(sector);
                    }
                }
                Array.isArray(hidra.actuadores) && hidra.actuadores.forEach(actuadorHidra => {
                    if (nodo.nodo_id === actuadorHidra.nodoId) {
                        if (!sectoresParaNodo.includes(sector)) {
                            sectoresParaNodo.push(sector);
                        }
                    }
                });
            });
        });
        nodo.sectores = sectoresParaNodo.length > 0 ? sectoresParaNodo : null;
        const sensorIndex = (nodo?.items?.findIndex((i) => i.label ==="SENSORS"))??-1+1;
        Array.isArray(nodo.items) && nodo.items.splice(sensorIndex, 0, {
            id: `sectores-${nodo.nodo_id}`, label: "ZONES"/* `ZONES (${nodo.sectores ? nodo.sectores.length : '0'})` */,
            items: nodo.sectores && nodo.sectores, etiqueta: "titulo", cantidad: `${nodo.sectores ? nodo.sectores.length : "0"}`
        });
    });

    if (!users) users = [];
    for (const user of users) {
        user.id = `permiso-${user.permiso_id}`;
    }
    if (!saas) saas = [];

    pozos.length && pozos.forEach((p) => {
        p.etiqueta = "hijo";
        p.id = `pozo-${p.dgas_mee_id}`;
    });

    pozos.sort((a, b) => (a.pozo_id < b.pozo_id) ? -1 : 1);

    /* const filterPozos = pozos.length && pozos.filter(p => p.pozo_id !== null); */

    /////////////
    const tituloNetwork = {
        id: "network",
        label: `NETWORK [ ${GWsEncontrados.length}/${data.nodes.length + GWsEncontrados.length} ]`,
        items: GWsEncontrados,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: `[ ${cantGw}-${cantNoGw + cantGw} ]`
    };

    const tituloSectores = {
        id: "sectores",
        label: `ZONES [ ${data.sectores.length} ]`,
        items: data.sectores,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: `[ ${data.sectores.length} ]`
    };

    const tituloEquipos = {
        id: "equipos",
        label: `PUMP SYSTEMS [ ${data.equipos.length} ]`,
        items: data.equipos,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: `[ ${data.equipos.length} ]`
    };

    const tituloGraficador = {
        id: "graficador",
        label: "GRAFICADOR",
        items: null,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: ""
    };

    const tituloSystemEvents = {
        id: "systemEvents",
        label: "SYSTEM EVENTS",
        items: null,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: ""
    };

    const tituloReportes = {
        id: "reports",
        name: "AUTOMATIC REPORTS",
        subChild: null,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: ""
    };

    const tituloMarketplace = {
        id: "marketplace",
        label: "MARKETPLACE",
        items: null,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: ""
    };
    const tituloExternal = {
        id: "external",
        label: "EXTERNAL",
        items: null,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: ""
    };
    const tituloIntegradores = {
        id: "integradores",
        label: "INTEGRADORES",
        items: null,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: ""
    };

    const treeNodes = [tituloNetwork, tituloSectores, tituloEquipos];

    const externos = data.externalNodes.map((id) => {
        const apiTercero = data.nodes.find(n => n.nodo_id === id);
        if (apiTercero) {
            apiTercero.id = `thirdparties-${apiTercero.nodo_id}`;
        }
        return apiTercero;
    });

    if (externos.length) treeNodes.push({
        id: "externos",
        label: `3RD PARTY APIS [ ${externos.length} ]`,
        items: externos,
        nivel: 1,
        etiqueta: "titulo",
        cantidad: `[ ${externos.length} ]`
    });

    treeNodes.push({
        id: "usuarios",
        label: `USERS [ ${users.length} ]`,
        items: users,
        etiqueta: "titulo",
        nivel: 1,
        cantidad: `[ ${users.length} ]`
    });
    /*
  if (saas.length) treeNodes.push({
    id: "sass",
    label: `APIS [ ${saas.length} ]`,
    etiqueta: "titulo",
    nivel: 1,
    cantidad: `[ ${saas.length} ]`
  });
*/
    treeNodes.push(
        {
            id: "rawdata",
            label: "RAW DATA",
            items: null,
            etiqueta: "titulo",
            nivel: 1,
            cantidad: ""
        });

    if (can_billing) { //MODIFY
        treeNodes.push(
            {
                id: "billingparams",
                label: "PARAMETROS COMERCIALES",
                items: null,
                etiqueta: "titulo",
                nivel: 1,
                cantidad: ""
            });
    }
    if (pozos.length) {
        treeNodes.push(
            {
                id: "pozosdga",
                label: `ENVIOS DGA [ ${pozos.length} ]`,
                items: pozos,
                etiqueta: "titulo",
                nivel: 1,
                cantidad: `[ ${pozos.length} ]`
            });
    }

    treeNodes.push(tituloGraficador);
    if(can_edit){
        if(enableMarketplace){
            treeNodes.push(tituloMarketplace);
        }
        treeNodes.push(tituloIntegradores);
        treeNodes.push(tituloExternal);
    }
    treeNodes.push(tituloSystemEvents);
    treeNodes.push(tituloReportes);

    return treeNodes;
};

const connections = (dataJerarquia, dataEquipos) => {
    const tipoActuador = [];
    const tipoSensor = [];
    dataJerarquia?.forEach((expansion) => {
        expansion?.puertos?.forEach((x) => {
            if (x && x.sensor) {
                let sensorTipo;
                if (x.sensor.tipo) {
                    sensorTipo = x.sensor.tipo;
                    if (!tipoSensor.some((r) => r.tipo === sensorTipo)) {
                        tipoSensor.push({ tipo: sensorTipo, cantidad: 1 });
                    } else {
                        const index = tipoSensor.findIndex((r) => r.tipo === sensorTipo);
                        tipoSensor[index].cantidad += 1;
                    }
                } else {
                    if(typeof x.sensor ==="object"){
                        sensorTipo = x.sensor.tipo??"No_type";
                        if (!tipoSensor.some((r) => r.tipo === sensorTipo)) {
                            tipoSensor.push({ tipo: sensorTipo, cantidad: 1 });
                        } else {
                            const index = tipoSensor.findIndex((r) => r.tipo === sensorTipo);
                            tipoSensor[index].cantidad += 1;
                        }

                    }else{
                        Array.isArray(x.sensor) && x.sensor.forEach((s) => {
                            sensorTipo = s.tipo;
                            if (!tipoSensor.some((r) => r.tipo === sensorTipo)) {
                                tipoSensor.push({ tipo: sensorTipo, cantidad: 1 });
                            } else {
                                const index = tipoSensor.findIndex((r) => r.tipo === sensorTipo);
                                tipoSensor[index].cantidad += 1;
                            }
                        });

                    }
                }
            }

            if (x && x.actuador) {
                if (!dataEquipos) {
                    const actuadorTipo = x.actuador.sist_tipo_actuador_id;
                    const asociacionHa = x.actuador.hidraulicas[0].tipo_asociacion_ha;
                    if (!tipoActuador.some((r) => r.tipo === actuadorTipo && r.sub_tipo === asociacionHa)) {
                        tipoActuador.push({ tipo: actuadorTipo, sub_tipo: asociacionHa, cantidad: 1 });
                    } else {
                        const index = tipoActuador.findIndex((r) => r.tipo === actuadorTipo && r.sub_tipo === asociacionHa);
                        tipoActuador[index].cantidad += 1;
                    }

                } else {
                    const actuadorTipo = x.actuador.sist_tipo_actuador_id;
                    const actuadorId = x.actuador.actuador_id;
                    let asociacionHa;
                    dataEquipos.equipos?.forEach((equipo) => {
                        equipo.hidraulicas?.forEach((hidraulica) => {
                            const tipoAsociacionHa = hidraulica.tipo_asociacion_ha;
                            let asociacionActuador;
                            if (tipoAsociacionHa) asociacionActuador = tipoAsociacionHa[actuadorId];
                            if (asociacionActuador) asociacionHa = asociacionActuador;
                            // si no encuentra asociacionHa en equipos, se debe setear asociacionHa a la que viene por defecto
                            // en el array de hidraulicas del actuador especifico
                            else {
                                if (x.actuador.hidraulicas?.length) asociacionHa = x.actuador.hidraulicas[0].tipo_asociacion_ha;
                            }
                        });
                    });

                    if (!tipoActuador.some((r) => r.tipo === actuadorTipo && r.sub_tipo === asociacionHa)) {
                        tipoActuador.push({ tipo: actuadorTipo, sub_tipo: asociacionHa, cantidad: 1 });
                    } else {
                        const index = tipoActuador.findIndex((r) => r.tipo === actuadorTipo && r.sub_tipo === asociacionHa);
                        tipoActuador[index].cantidad += 1;
                    }
                }
            }
        });
    });

    return [{ tipoActuador, tipoSensor }];
};

const Farms = ({ mapRef, selectorRef, accountsData, usuarioInfo, menuIsOpen, fields }) => {
    const location = useLocation();
    const { farm } = getParams(location);
    const [dataSource, dispatch] = useContext(FarmContext);
    const [farmId, setFarmId] = useState(farm);
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();
    if (farmId !== farm) setFarmId(farm);
    useEffect(() => {
        async function getDatos() {
            try {
                if (!farmId) return;
                const farmGeneral = `/farms/${farmId}/general`;
                const param = `/farms/${farmId}`;
                const users = `/users/${farmId}`;
                const saas = `/users/${farmId}/api`;
                const fenologia = `/farms/${farmId}/plantillasFenologia`;
                dispatch({ type: "mark", params: farmId });
                dispatch({ type: "set", params: ds => ({ ...ds, loading: true }) });
                const [newData, datosFarm, usersData, saasData, fenologiaData] = await Promise.all([
                    RestAPI.get("apiAdminDropControl", param).then((resultado) => {
                        if (Array.isArray(resultado && resultado.network) && Array.isArray(resultado.nodes)) {
                            resultado.network = resultado.network.filter((gwId) => {
                                if (gwId) return true;

                                // When there is no gateway, al nodes without modem are added
                                // as a child of a dummy node with nodo_id 0.
                                const gwIdx = resultado.nodes.findIndex(nodo => nodo.nodo_id === gwId);
                                if (gwIdx === -1) return false;
                                const gw = resultado.nodes[gwIdx];
                                resultado.nodes.splice(gwIdx, 1);

                                // If the dummy node has no children, remove it from network.
                                if ((!gw.childs || !gw.childs.length)) {
                                    return false;
                                } else {
                                    gw.nombre = "No Gateway";
                                    gw.tipo = 0;
                                }
                                return true;
                            });
                        }

                        if (Array.isArray(resultado.nodes)) resultado.nodes.forEach(nodo => {
                            if (!nodo) return;
                            nodo.nombre = nodo.nombre && nodo.nombre.split("[")[0].trim();
                            const portsData = formatPortsData(nodo);
                            nodo.ports = portsData;
                            delete nodo.puertos_tarjetas_expansion;

                            const jerarquia = nodo.ports.hierarchy;
                            const datos = connections(jerarquia, resultado)[0];
                            nodo.data = datos;
                            const cantidadSensores = datos.tipoActuador.reduce((total, sensor) => {
                                return total + sensor.cantidad;
                            }, 0);
                            const cantidadActuadores = datos.tipoSensor.reduce((total, sensor) => {
                                return total + sensor.cantidad;
                            }, 0);
                            nodo.cantidad = cantidadSensores + cantidadActuadores;
                        });

                        if (Array.isArray(resultado.sectores) && resultado.areaInfo) {
                            const { formula_to_base: formula1, formula_from_kilo_base: formula2 } = resultado.areaInfo;
                            resultado.sectores.forEach(sector => {
                                if (sector && sector.superficie) {
                                    // we can use formula2( formula1( sup / 10.000 ) )
                                    // instead of formula2( formula1( sup ) / 10.000 ) for area
                                    sector.superficie = applyFormulas(sector.superficie / 10000, formula1, formula2, AREA_FORMAT_KEY);
                                }
                            });
                        }

                        const areaInfo = resultado.areaInfo;
                        if (resultado.equipos) resultado.equipos.sort(({ nombre: a }, { nombre: b }) => (a < b) ? -1 : 1);
                        const origData = cloneDeep(resultado);
                        return { data: resultado, areaInfo, original: origData };
                    }),
                    RestAPI.get("apiAdminDropControl", farmGeneral).then((datosFarm) => {
                        if (datosFarm) datosFarm.mapa_json = datosFarm.mapa_json && JSON.parse(datosFarm.mapa_json);
                        if (!datosFarm.prefijo) datosFarm.prefijo = defaultPrefijo;
                        return datosFarm;
                    }),
                    RestAPI.get("apiAdminDropControl", users).then(json => {
                        if (json && !json.users) json.users = [];
                        return json;
                    }).catch(err => console.error(err)),
                    RestAPI.get("apiAdminDropControl", saas).then(json => {
                        if (json && !json.users) json.users = [];
                        return json;
                    }).catch(err => console.error(err)),
                    RestAPI.get("apiAdminDropControl", fenologia).then(json => {
                        return json;
                    }).catch(err => console.error(err))
                ]);
                if (newData) {
                    newData.data = formatData(newData.data, newData.areaInfo, usersData && usersData.users, saasData && saasData.users,
                        datosFarm && datosFarm.mapa_json, datosFarm && datosFarm.huso_horario, datosFarm && datosFarm.can_edit, usuarioInfo && usuarioInfo.comercial);
                    datosFarm.areaInfo = newData.areaInfo;
                    delete newData.areaInfo;
                }
                if (datosFarm.areaInfo) {
                    const { formula_from_kilo_base: formula, kilo_unidad, formula_to_base: formulaTo } = datosFarm.areaInfo;
                    datosFarm.unidadArea = kilo_unidad || "";
                    if (datosFarm.sup_calcs && datosFarm.sup_calcs.total > 0) {
                        datosFarm.calculatedArea = applyFormula(datosFarm.sup_calcs.total, formula);
                    } else if (datosFarm.sum_bloques) {
                        datosFarm.calculatedArea = applyFormulas(datosFarm.sum_bloques / 10000, formulaTo, formula, "number.dec.4");
                    }
                }

                datosFarm.fenologia = fenologiaData?.plantillas;
                datosFarm.tiposRenovacion = fenologiaData?.tiposRenovacion;
                datosFarm.paletasFenologia = fenologiaData?.paletas;

                selectorRef.current && selectorRef.current.updateFarmInfo(datosFarm);
                dispatch({ type: "set", params: { loading: false, ...newData, dataFarm: datosFarm, users: usersData, saas: saasData, formated: true } });
                setLoading(false);
            } catch (err) {
                dispatch({ type: "set", params: { loading: false, data: null, dataFarm: null, original: null, users: null, saas: null, formated: false } });
                console.error("error", err);
            }
        }
        setLoading(true);
        getDatos();
    }, [farmId]);

    const { dataFarm, original, users, saas, formated } = dataSource;
    useEffect(() => {
        if (original && !formated) {
            const cloneData = cloneDeep(original);
            const formatted = formatData(cloneData, original.areaInfo, users && users.users, saas && saas.users,
                dataFarm && dataFarm.mapa_json, dataFarm && dataFarm.huso_horario, dataFarm && dataFarm.can_edit, usuarioInfo && usuarioInfo.comercial);
            dispatch({
                type: "set", params: ds => {
                    if (ds.original !== original || formated) return ds;
                    return { ...ds, data: formatted, formated: true };
                }
            });
        }
    }, [original, formated]);
    return (
        <>
            {loading === true && <div className='row-container'><LoadingDialog /></div>}
            {dataFarm?.acceso_limitado &&
        <div className="card bg-danger">
            <div className="card-body">
                <div className='row align-items-center'>
                    <div className='col-1'>
                        <FontAwesomeIcon size="2x" icon={faExclamationCircle} style={{ color: "white" }} />
                    </div>
                    <div className='col-9'>
                        <span className='row-header-text'>{t("farms.acceso_limitado")}</span>
                    </div>
                </div>
            </div>
        </div>}
            <ErrorBoundary name="Farm" resetOnChange>
                {dataSource?.dataFarm?.estado_billing === "READY" && dataSource?.dataFarm?.admin_billing && dataSource?.dataFarm?.es_cliente?
                    <Routes>
                        <Route path="/" element={<BillingPolicyAndTerms dataSource={dataSource?.dataFarm} />} />
                    </Routes>
                    :<Routes>
                        <Route path="/actuator" element={<Actuator />} />
                        <Route path="/sensor" element={
                            dataFarm && <Sensor dataFarm={dataFarm} />
                        }
                        />
                        <Route path="/hydraulic" element={
                            dataFarm && <Hydraulic dataFarm={dataFarm} />
                        }
                        />
                        <Route path="/pumpsys" element={
                            dataFarm && <Pump dataFarm={dataFarm} mapRef={mapRef} />
                        }
                        />
                        <Route path="/node" element={
                            dataFarm && <Node dataFarm={dataFarm} mapRef={mapRef} />
                        }
                        />
                        <Route path="/zone" element={
                            dataFarm && <Zone dataFarm={dataFarm} mapRef={mapRef} accountsData={accountsData} />
                        }
                        />
                        <Route path="/thirdparties" element={
                            dataFarm && <APITerceros dataFarm={dataFarm} />
                        }
                        />
                        <Route path="/user" element={
                            dataFarm && <User />
                        }
                        />
                        <Route path="/users" element={
                            <Users dataFarm={dataFarm} />
                        }
                        />
                        {/*<Route path="/sass" element={
              dataFarm && <SaSServices dataFarm={dataFarm} />
            } />*/}
                        <Route path="/rawdata" element={<RAWData />} />
                        {usuarioInfo?.comercial && //MODIFY
              <Route path="/billingparams" element={<BillingParams dataSource={dataSource?.dataFarm} />} />}
                        <Route path="/well" element={
                            dataFarm && <DGAWell dataFarm={dataFarm} pozosInfo={original.pozos} nodosInfo={original.nodes} usuarioInfo={usuarioInfo} menuIsOpen={menuIsOpen} />
                        }
                        />
                        <Route path="/customchart" element={
                            dataFarm && <Graficador dataFarm={dataFarm} nodes={original.nodes} menuIsOpen={menuIsOpen} />
                        }
                        />
                        {
                            dataFarm?.can_edit &&
                        <Route path="/integrators" element={
                            dataFarm && <Integradores />
                        }
                        />
                        }
                        {
                            dataFarm?.can_edit &&
                        <Route path="/external" element={
                            dataFarm && <ExternalProvider sectorsInfo={original.sectores} />
                        }
                        />
                        }
                        <Route path="/mapconfig" element={
                            dataFarm && <MapaConfig dataFarm={dataFarm} nodesInfo={original.nodes} sectorsInfo={original.sectores} mapRef={mapRef} isLoaded={!dataSource.loading} />
                        }
                        />
                        <Route path="/imagealigner" element={
                            dataFarm && <ImageAligner dataFarm={dataFarm} nodesInfo={original.nodes} sectorsInfo={original.sectores} mapRef={mapRef} isLoaded={!dataSource.loading} />
                        }
                        />
                        <Route path="/systemEvents" element={
                            dataFarm && <SystemEvents dataFarm={dataFarm} />
                        }
                        />
                        <Route path="/reports" element={
                            dataFarm && <Reports dataFarm={dataFarm} sectorsInfo={original.sectores} accountsData={accountsData} fields={fields} usuarioInfo={usuarioInfo} />
                        }
                        />
                        {
                            enableMarketplace && dataFarm?.can_edit &&
                <Route path="/marketplace" element={
                    dataFarm && <Marketplace />
                }
                >
                    <Route index element={<MarketplaceTabs />} />
                    <Route path=":id" element={
                        dataFarm && <ConnectionDetail usuarioInfo={usuarioInfo} />
                    }
                    />
                    <Route path="connected/:id" element={
                        dataFarm && <ConnectionDetail usuarioInfo={usuarioInfo} />
                    }
                    />
                </Route>
                        }
                        { dataFarm?.features_dropcontrol?.fenologia?.esta_habilitado &&
              <Route path="/phenology" element={
                  dataFarm && <Phenology dataFarm={dataFarm} sectorsInfo={original.sectores} />
              }
              />}
                        <Route path="/" element={
                            dataFarm && <FarmInfo dataFarm={dataFarm} mapRef={mapRef} isLoaded={!dataSource.loading}
                                setDataFarm={({ data }) => {
                                    dispatch({ type: "set", params: ds => ({ ...ds, dataFarm: data }) });
                                    selectorRef.current && selectorRef.current.updateFarmInfo(data);
                                }}
                            />
                        }
                        />
                    </Routes>}
            </ErrorBoundary>
        </>
    );
};

const claseTipoNodo = (tipo, esGw) => {
    if (tipo === 1 || tipo === 2) {
        if (esGw) {
            return "gw-nodo-x1";
        } else {
            return "nodo-x1";
        }
    }

    if (tipo === 7 || tipo === 8) {
        if (esGw) {
            return "gw-nodo-c1";
        } else {
            return  "nodo-c1";
        }
    }

    if (tipo === 9) {
        return  "nodo-m1";
    }

    if (tipo === 10) {
        return "nodo-m1-3g";
    }

    if (tipo === 16) {
        if (esGw) {
            return "gw-nodo-v1";
        } else {
            return  "nodo-v1";
        }
    }
};

const translateName = (t, dato, objeto) => {
    if (dato.etiqueta === "titulo") {
        const transName = t(`farms.etiqueta.${objeto}`);
        if (dato.nivel === 1) {
            return `${transName} ${dato.cantidad}`;
        }
        return transName;
    } else if (dato.etiqueta === "hijo") {
        if (objeto === "sector") {
            const tipos =
        Array.isArray(dato.tipo) &&
        dato.tipo.map((tipo) => tipo && t(`tipo.sector.${tipo}Cap`));
            const resultado = (Array.isArray(tipos) && tipos.join(" - ")) || "";
            return resultado;
        }
    }
};

const switchTipoHidraulica = (t, param, es_monitoreo, tiene_controlador) => {
    switch (param) {
        case 1:
            //return 'PUMP';
            return t("farms.tipoHidraulica.pump");
        case 2:
            //return 'VALVE';
            return es_monitoreo ? t("farms.tipoHidraulica.monitoringValve") : t("farms.tipoHidraulica.valve");
        case 3:
            //return 'WATER SOURCE';
            return t("farms.tipoHidraulica.waterSource");
        case 4:
            //return 'TANK';
            return t("farms.tipoHidraulica.tank");
        case 5:
            //return 'PH INJECTOR';
            return t("farms.tipoHidraulica.phInjector");
        case 6:
            //return 'FERTILIZER INJECTOR';
            if(tiene_controlador){
                return t("farms.tipoHidraulica.fertiwise");
            }else {
                return t("farms.tipoHidraulica.fertilizerInjector");
            }
        case 9:
            //return 'WELL';
            return t("farms.tipoHidraulica.well");
        case 14:
            //return 'VFD';
            return t("farms.tipoHidraulica.vfd");
        case 16:
            //return 'BACKFLUSH STATION';
            return t("farms.tipoHidraulica.bfStation");
        default:
            return "";
    }
};

const nodeNav = (dato, options, treeRef, navigate, history, farmId) => {
    try {
        const nodo_id = dato && dato.id;

        /** @type {HTMLDivElement} */
        const tree = treeRef.current.getElement();

        const expanded = { state: { expanded: [] } };
        const panelId = options?.props?.panelId ?? "";
        const prefix = panelId + "_";
        let elType, elId, currentNode = tree.querySelector(`#${prefix}${nodo_id}`);

        while (currentNode) {
            elId = currentNode.getAttribute("id");
            if (elId) expanded.state.expanded.unshift(elId.replace(prefix, ""));

            currentNode = currentNode.parentNode;
            while (currentNode) {
                elType = currentNode.getAttribute("data-pc-section");

                if (elType === "menuitem") {
                    break;
                } else if (currentNode === tree) {
                    currentNode = null;
                } else {
                    currentNode = currentNode.parentNode;
                }
            }
        }

        // At this point we have all of the elements, except for the first one (because that one is not a menuitem, but a section)
        if (expanded.state.expanded[0] && Array.isArray(options?.props?.menuProps?.model)) {
            const model = options?.props?.menuProps?.model;
            const firstChildId = expanded.state.expanded[0];
            if (Array.isArray(model)) {
                const firstItem = model.find(i => {
                    return Array.isArray(i?.items) && i.items.some(c => c.id === firstChildId);
                });

                if (firstItem) expanded.state.expanded.unshift(firstItem.id);
            }

            // set base ids
            expanded.state.expanded = expanded.state.expanded.map(i => {
                const [objeto_id, parent] = i?.split("_") || [];
                return objeto_id;
            });
        }

        const [objeto_id, parent] = (nodo_id && nodo_id.split("_")) || [];
        const [objeto, id] = (objeto_id && objeto_id.split("-")) || [];

        const location = history.location;
        const prevSearch = location && location.search;
        const prevParams = new URLSearchParams(prevSearch);

        if (objeto === "actuador" && dato.etiqueta !== "titulo") {
            const newSearch = `?farm=${farmId}&actuator=${id}`;
            if (prevParams.get("actuator") !== id) {
                navigate({ pathname: "/farms/actuator", search: newSearch }, expanded);
            }
        } else if (objeto === "sensor" && dato.etiqueta !== "titulo") {
            const newSearch = `?farm=${farmId}&sensor=${id}`;
            if (prevParams.get("sensor") !== id) {
                navigate({ pathname: "/farms/sensor", search: newSearch }, expanded);
            }
        } else if (objeto === "hidraulica" && dato.etiqueta !== "titulo") {
            const newSearch = `?farm=${farmId}&hydraulic=${id}`;
            if (prevParams.get("hydraulic") !== id) {
                navigate({ pathname: "/farms/hydraulic", search: newSearch }, expanded);
            }
        } else if (objeto === "equipo" && dato.etiqueta !== "titulo") {
            const newSearch = `?farm=${farmId}&pumpsys=${id}`;
            if (prevParams.get("pumpsys") !== id) {
                navigate({ pathname: "/farms/pumpsys", search: newSearch }, expanded);
            }
        } else if (objeto === "sector" && dato.etiqueta !== "titulo") {
            const newSearch = `?farm=${farmId}&zone=${id}`;
            if (prevParams.get("zone") !== id) {
                navigate({ pathname: "/farms/zone", search: newSearch }, expanded);
            }
        } else if (objeto === "nodo") {
            const newSearch = `?farm=${farmId}&node=${id}`;
            if (prevParams.get("node") !== id) {
                navigate({ pathname: "/farms/node", search: newSearch }, expanded);
            }
        } else if (objeto === "thirdparties") {
            const newSearch = `?farm=${farmId}&thirdparties=${id}`;
            if (prevSearch !== newSearch) {
                navigate({ pathname: "/farms/thirdparties", search: newSearch }, expanded);
            }
        } else if (objeto === "permiso") {
            const newSearch = `?farm=${farmId}&user=${id}`;
            if (prevSearch !== newSearch) {
                navigate({ pathname: "/farms/user", search: newSearch }, expanded);
            }
        } else if (objeto === "usuarios") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/users", location.pathname)) {
                navigate({ pathname: "/farms/users", search: newSearch }, expanded);
            }
        } /*else if (objeto === "sass") {
      const newSearch = `?farm=${farmId}`;
      if (!matchPath("/farms/sass", location.pathname)) {
        navigate({ pathname: "/farms/sass", search: newSearch }, expanded);
      }
    }*/else if (objeto === "rawdata") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/rawdata", location.pathname)) {
                navigate({ pathname: "/farms/rawdata", search: newSearch }, expanded);
            }
        }else if (objeto === "pozo") {
            const newSearch = `?farm=${farmId}&well=${id}`;
            if (prevSearch !== newSearch) {
                navigate({ pathname: "/farms/well", search: newSearch }, expanded);
            }
        } else if (objeto === "graficador") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/customchart", location.pathname)) {
                navigate({ pathname: "/farms/customchart", search: newSearch }, expanded);
            }
        }else if (objeto === "integradores") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/integrators", location.pathname)) {
                navigate({ pathname: "/farms/integrators", search: newSearch }, expanded);
            }
        }else if (objeto === "external") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/external", location.pathname)) {
                navigate({ pathname: "/farms/external", search: newSearch }, expanded);
            }
        } else if (objeto === "systemEvents") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/systemEvents", location.pathname)) {
                navigate({ pathname: "/farms/systemEvents", search: newSearch }, expanded);
            }
        } else if (objeto === "reports") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/reports", location.pathname)) {
                navigate({ pathname: "/farms/reports", search: newSearch }, expanded);
            }
        } else if (objeto === "billingparams") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/billingparams", location.pathname)) {
                navigate({ pathname: "/farms/billingparams", search: newSearch }, expanded);
            }
        } else if (objeto === "marketplace") {
            const newSearch = `?farm=${farmId}`;
            if (!matchPath("/farms/marketplace", location.pathname)) {
                navigate({ pathname: "/farms/marketplace", search: newSearch }, expanded);
            }
        } else {
            return false;
        }
        return true;
    } catch (error) {
        console.error(error);
    }
};

const SelectedItemContext = createContext(null);

const TranslateElementsPrime = ({ datos, options, treeRef, history, setSelectedItem, onMouseEnterItem, onMouseLeaveItem }) => {
    const [dataSource] = useContext(FarmContext);
    const { t, i18n } = useTranslation();
    const langs = getBaseLangs(i18n);
    const navigate = useNavigate();

    const selectedItem = useContext(SelectedItemContext);
    const [objeto_id] = datos?.id?.split("_") ?? [];

    if (datos === null || datos === undefined || datos.id === null) return null;
    const [objeto] = (datos.id && datos.id.split("-")) || [];
    let content = [];
    switch (objeto) {
        case "network":{
            content = <><span className='menu-icons-prime red-oscuro' /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "nodo":{
            content = <>
                <span className={`menu-icons-prime ${claseTipoNodo(datos.tipo, datos.is_gw)}`}> </span>
                <p className='orden'>{datos.nombre}</p>
                <div className='cantidad'>
                    {datos.noData && <span id="icon-white-blue" className="fa-stack statusIcon midIcon" title={t("status.noData")}>
                        <a dangerouslySetInnerHTML={{ __html: noDataWhiteBlue }} />
                    </span>}
                    {datos.noLocation && <span id="icon-white-blue" className="fa-stack statusIcon midIcon" title={t("maps.nodata")}>
                        <a dangerouslySetInnerHTML={{ __html: noLocationWhiteBlue }} />
                        <a dangerouslySetInnerHTML={{ __html: iconBanWhiteBlue }} />
                    </span>}
                    {datos.etiqueta === "GW" && datos.cantNodos > 0 && <span className="position-relative statusIcon">{datos.cantNodos}</span>}
                </div>
                <p className='tipo'>{switchTipoNodo(datos.tipo)}</p>
            </>;
            break;
        }
        case "actuadores":{
            content = <><span className='menu-icons-prime actuadores-oscuro' /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "actuador":{
            content = <><span className='menu-icons-prime actuador-oscuro' /><p className='orden-solo'>{datos.tipo_asociacion_ha && t(`tipo.asociacion_ha.${datos.tipo_asociacion_ha}`, "")} - {datos.hidraulica || datos.nombre} </p></>;
            break;
        }
        case "hidraulicas":{
            content = <>
                <span className='menu-icons-prime hidraulicas-oscuro' />
                <p className='orden uppercase'> {translateName(t,datos, objeto)} </p>
                {datos.etiqueta==="titulo" ?
                    <p className='tipo uppercase'>
                        {datos.tipoFuente > 0 || datos.tipoBomba > 0 || datos.tipoEstanque > 0 || datos.tipoInyectorF ||
              datos.tipoInyectorPH > 0 || datos.tipoRetrolavado > 0 || datos.tipoValvula > 0 || datos.tipoPozo > 0 || datos.tipoVFD > 0 ||
              datos.tipoValvulaMonitoreo > 0 ?
                            <>
                                {datos.tipoFuente > 0 ? `${t("farms.tipoHidraulica.waterSource")}(${datos.tipoFuente}) ` : ""}
                                {datos.tipoBomba > 0 ? `${t("farms.tipoHidraulica.pump")}(${datos.tipoBomba}) ` : ""}
                                {datos.tipoEstanque > 0 ? `${t("farms.tipoHidraulica.tank")}(${datos.tipoEstanque}) ` : ""}
                                {datos.tipoInyectorF > 0 ? `${t("farms.tipoHidraulica.fertilizerInjector")}(${datos.tipoInyectorF}) ` : ""}
                                {datos.tipoInyectorPH > 0 ? `${t("farms.tipoHidraulica.phInjector")}(${datos.tipoInyectorPH}) ` : ""}
                                {datos.tipoRetrolavado > 0 ? `${t("farms.tipoHidraulica.bfStation")}(${datos.tipoRetrolavado}) ` : ""}
                                {datos.tipoValvula > 0 ? `${t("farms.tipoHidraulica.valve")}(${datos.tipoValvula}) ` : ""}
                                {datos.tipoValvulaMonitoreo > 0 ? `${t("farms.tipoHidraulica.monitoringValve")}(${datos.tipoValvulaMonitoreo}) ` : ""}
                                {datos.tipoPozo > 0 ? `${t("farms.tipoHidraulica.well")}(${datos.tipoPozo}) ` : ""}
                                {datos.tipoVFD > 0 ? `${t("farms.tipoHidraulica.vfd")}(${datos.tipoVFD}) ` : ""}
                            </>
                            :
                            "-"}
                    </p>
                    : null}
            </>;
            break;
        }
        case "hidraulica":{
            content = <>
                <span className='menu-icons-prime hidraulicas-oscuro' />
                <p className='orden'> {datos.nombre} </p>
                {datos.etiqueta !== "titulo" ?
                    <p className='tipo uppercase'>{switchTipoHidraulica(t, datos.tipo, datos.es_monitoreo, datos.tiene_controlador)}</p>
                    : null}
            </>;
            break;
        }
        case "sectores":{
            content = <><span className='menu-icons-prime sectores-oscuro' /><p className='orden uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "sector":{
            content = <>
                <span className='menu-icons-prime sector-oscuro' />
                <p className='orden'> {datos.nombre}{datos.superficie ? ` (${t("general.number", { value: datos.superficie })} ${datos.kilo_unidad})` : ""} </p>
                <div className='cantidad'>
                    {datos.noLocation && <span id="icon-white-blue" className="fa-stack statusIcon" title={t("maps.nodata")}>
                        <a dangerouslySetInnerHTML={{ __html: noLocationWhiteBlue }} />
                        <a dangerouslySetInnerHTML={{ __html: iconBanWhiteBlue }} />
                    </span>}
                </div>
                {datos.etiqueta === "hijo" ?
                    <p className='tipo'>{translateName(t,datos, objeto)}</p>
                    : null}
            </>;
            break;
        }
        case "sensores":{
            content = <><span className='menu-icons-prime sensores-oscuro' /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "sensor":{
            content = <>
                <span className='menu-icons-prime sensor-oscuro' />
                <p className='orden'>{datos.nombre_mutate ? (datos.tipo && (datos.tipo?.[langs?.[0]] || datos.tipo?.[langs?.[1]]) || datos.nombre) : datos.nombre}{datos.profundidad ? ` (${t("general.number", { value: datos.profundidad })} ${datos.centi_unidad})` : ""} </p>
                <p className='tipo'>{datos.nombre_mutate ? "" : (datos.tipo?(datos.tipo?.[langs?.[0]] || datos.tipo?.[langs?.[1]]):`sensor.${datos.sensor_id}`)} </p>
            </>;
            break;
        }
        case "externos":{
            content = <><FontAwesomeIcon className="faIcon" icon={faCubes} size="lg" /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "thirdparties":{
            content = <><FontAwesomeIcon className="faIcon absPos" icon={faCubes} size="lg" /><p className='orden'> {datos.nombre} </p><p className='tipo'>{switchTipoNodo(datos.tipo)}</p></>;
            break;
        }
        case "rawdata":{
            content = <><FontAwesomeIcon className="faIcon" icon={faDownload} size="lg" /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "billingparams":{
            content = <><FontAwesomeIcon className="faIcon" icon={faSlidersH} size="lg" /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "pozosdga":{
            content = <><FontAwesomeIcon className="faIcon" icon={faTint} size="lg" /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "pozo":{
            content = <><span className='menu-icons-prime hidraulicas-oscuro' /><p className='orden-solo'> {datos.nombre} </p></>;
            break;
        }
        case "systemEvents":{
            content = <><FontAwesomeIcon className="faIcon" icon={faFileAlt} size="lg" /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "reports":{
            content = <><FontAwesomeIcon className="faIcon" icon={faPoll} size="lg" /><p className='orden-solo uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "equipos":{
            content = <><span className='menu-icons-prime equipos-oscuro' /><p className='orden uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "equipo":{
            content = <><span className='menu-icons-prime equipo-oscuro' /><p className='orden-solo'> {datos.nombre} </p></>;
            break;
        }
        case "usuarios":{
            content = <><span className='menu-icons-prime usuarios-oscuro' /><p className='orden uppercase'> {translateName(t,datos, objeto)} </p></>;
            break;
        }
        case "integradores":{
            content = <><FontAwesomeIcon className="faIcon" icon={faTruck} size="lg" /><p className='orden-solo uppercase'>{translateName(t,datos, objeto)}</p></>;
            break;
        }
        case "permiso":{
            content = <><span className='menu-icons-prime usuario-oscuro' /><p className='orden'>{datos.nombre} {datos.apellido_paterno} {datos.apellido_materno}</p><p className='tipo'>{datos.email}</p></>;
            break;
        }
        case "nodos":{
            content = <><span className='menu-icons-prime nodo-oscuro' /><p className='orden-solo uppercase'>{translateName(t,datos, objeto)}</p></>;
            break;
        }
        case "graficador":{
            content = <><span className='menu-icons-prime graficador-oscuro' /><p className='orden-solo uppercase'>{translateName(t,datos, objeto)}</p></>;
            break;
        }
        case "external":{
            content = <><span className='menu-icons-prime external-providers-oscuro' /><p className='orden-solo uppercase'>{translateName(t,datos, objeto)}</p></>;
            break;
        }
        case "marketplace":{
            content = <><FontAwesomeIcon className="faIcon" icon={faStore} size="lg" /><p className='orden-solo uppercase'>{translateName(t,datos, objeto)}</p></>;
            break;
        }
    }

    return(
        <div className="toogleIconwrapper" onMouseEnter={onMouseEnterItem} onMouseLeave={onMouseLeaveItem} >
            {
                datos.id.includes("-")?
                    datos?.items?.length>0 ?
                        <div className="toogleIcon"><i style={{ fontSize: "1rem", paddingTop:"0.5rem" }} className={options.active?"pi pi-angle-down":"pi pi-angle-right"} onClick={options.onClick}  /></div>
                        : <div className='iconFiller' />
                    :null
            }
            <div
                onClick={(args) => {
                    if (datos?.items?.length>0 && !datos.id.includes("-") && objeto !== "usuarios") {
                        options.onClick(args);
                    } else {
                        const [objeto_id] = datos?.id?.split("_") ?? [];
                        const navigated = nodeNav(datos, options, treeRef, navigate, history, dataSource?.dataFarm?.campo_id);
                        if (navigated) setSelectedItem(objeto_id);
                    }
                    args.stopPropagation();
                }}
                className={`treeview-texts-prime
          ${datos.etiqueta === "titulo" && datos.nivel !== 1 ? "title" : ""}
          ${objeto === "nodo" ? "node" : ""}
          ${(objeto_id && selectedItem === objeto_id) ? "activeNode":""}`}
            >
                {content}
                {(datos.items && !datos.id.includes("-")) ? <i className={options.active ? "pi pi-minus" : "pi pi-plus"} onClick={(args) => {
                    options.onClick(args);
                    args.stopPropagation();
                }}
                /> : null}
                {(datos.etiqueta === "titulo" && datos.id.search("-") !== -1) ? <div className="cantidad"><span className="position-relative statusIcon">{datos.cantidad}</span></div> : null}
            </div>
        </div>
    );
};

export const setTreeState = (menuTreeNodes, setExpandedKeys, setSelectedItem, locState) => {
    if (!Array.isArray(locState)) return;

    let i, j;
    let currentNode = null;
    let nextNode = { items: menuTreeNodes };

    const l = locState.length;
    const extraKeys = {};
    let validItem = null;

    for (i = 0; i < l; i++) {
        if (!nextNode) return;
        currentNode = nextNode;
        nextNode = null;

        if (!Array.isArray(currentNode.items)) return;

        for (j = 0; j < currentNode.items.length; j++) {
            const [objeto_id] = currentNode.items[j]?.id?.split("_") ?? [];

            if (objeto_id && (objeto_id === locState[i])) {
                nextNode = currentNode.items[j];
                if (!nextNode) return;

                if (i === (l - 1)) {
                    validItem = locState[i];
                } else {
                    extraKeys[nextNode.id] = true;
                }
                break;
            }
        }
    }

    setSelectedItem(validItem);
    setExpandedKeys?.(c => ({ ...c, ...extraKeys }));
};

const StateReseter = ({ menuTreeNodes, setExpandedKeys, setSelectedItem }) => {
    const action = useNavigationType();
    const location = useLocation();

    useEffect(() => {
        const { pathname, state } = location || {};

        if (state && (action === "POP" || state.updateTree)) {
            setTreeState(menuTreeNodes, setExpandedKeys, setSelectedItem, state && state.expanded);
        } else if (action && !matchPath("/farms/*", pathname)) {
            // clean selected nodes if, for example, we are in "/personal" or "/account"
            setSelectedItem(null);
        }

        putLog("new loc:" + pathname + ", action: " + action, 2);
    }, [menuTreeNodes, location, action]);

    return null;
};

const FarmTreeView = ({ history, onMouseEnterItem, onMouseLeaveItem }) => {
    const [dataSource] = useContext(FarmContext);
    const [menuTreeNodes, setMenuTreeNodes] = useState(null);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);

    const treeRef = useRef(null);

    const nodeTemplate = useMemo(() => {
        const FarmNode = (datos, options) => <TranslateElementsPrime datos={datos} options={options} history={history} treeRef={treeRef} setSelectedItem={setSelectedItem} onMouseEnterItem={onMouseEnterItem} onMouseLeaveItem={onMouseLeaveItem} />;
        return FarmNode;
    }, [onMouseEnterItem, onMouseLeaveItem]);

    const setTreeKeys = (treeNode, extraKey, level) => {
        const real_id = treeNode.original_id ?? treeNode.id;

        treeNode.original_id = real_id;
        treeNode.id = level>1 ? `${real_id}_${extraKey}` : real_id;
        treeNode.key = treeNode.id;
        treeNode.template = nodeTemplate;

        if (Array.isArray(treeNode.items)) {
            treeNode.items = treeNode.items.map((child, i) => {
                return setTreeKeys({ ...child }, `${extraKey}_${i}`, level + 1);
            });
        }
        return treeNode;
    };

    useEffect(() => {
        if (dataSource.data) {
            const newData = dataSource.data.map((treeNode) => {
                return setTreeKeys({ ...treeNode }, `${treeNode.id}`, 0);
            });
            setMenuTreeNodes(newData);
            dataSource.data = newData;
        }
    }, [dataSource]);

    return (
        <>
            <StateReseter menuTreeNodes={menuTreeNodes} setExpandedKeys={setExpandedKeys} setSelectedItem={setSelectedItem} />
            <Suspense fallback={<div className='row-container'><img className='img-loading' src={Loading} alt="Loading..." /></div>}>
                {
                    dataSource?.dataFarm?.estado_billing === "READY" && dataSource?.dataFarm?.admin_billing && dataSource?.dataFarm?.es_cliente?null:
                        <SelectedItemContext.Provider value={selectedItem}>
                            <PanelMenu
                                ref={treeRef}
                                model={menuTreeNodes}
                                id="farmPM"
                                expandedKeys={expandedKeys}
                                onExpandedKeysChange={setExpandedKeys}
                                multiple
                            />
                        </SelectedItemContext.Provider>
                }
            </Suspense>
        </>
    );
};

export { FarmTreeView };
export default withName(Farms, "FarmPage");
export { connections };
